import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@api/apiMemberArea';
import { IApiMTradingAccount } from '@api/interfaces/data-contracts';
import { TabsContent } from '@components-member/TradingAccounts/AccountDetails/AccountTabsContent';
import { IAccountDetailsProps } from '@components-member/TradingAccounts/interfaces';
import { CONSTANTS } from '@components-shared/constants';
import ErrorView from '@components-shared/errorView';
import { Preloader } from '@components-shared/preloader';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';

const TradingAccountDetails = (props: IAccountDetailsProps): JSX.Element | null => {
  const { accountStore, pspStore } = useMobxStore();

  const {
    userProfile,
    userInfo,
    onboardingConfiguration,
    generalSettings: { brokerParams },
  } = accountStore;

  const { match, location } = props;

  const { accountId } = match.params;
  const rootPath = match.url;

  const [account, setAccount] = useState<IApiMTradingAccount | null>(null);
  const [processing, setProcessing] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>('');

  const getDetails = useCallback(async (): Promise<void> => {
    try {
      const response = await api.ma.tradingAccountUser.getAccountInfo(Number(accountId));

      setAccount(response.data);
      setProcessing(false);
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
      setErrMsg(error.response.data);
      setProcessing(false);
    }
  }, [accountId]);

  useEffect(() => {
    void (async () => {
      await getDetails();
      await pspStore.fetchAllAccounts(CONSTANTS.TRADING_ACCOUNT_TYPE.REAL);
    })();
  }, [pspStore, getDetails]);

  if (processing) {
    return <Preloader />;
  }

  if (errMsg) {
    return (
      <div className="content">
        <ErrorView error={errMsg} />
      </div>
    );
  }

  if (!account || !onboardingConfiguration) {
    return null;
  }

  const { currency, balance, login, tradingAccountName, enabled } = account;

  const renderData = {
    userInfo,
    brokerParams,
    currency,
    balance,
    login,
    accountName: tradingAccountName,
    accountEnabled: enabled,
    processing,
    errMsg,
    pathName: location.pathname,
    accountPlatform: account.platformType,
  };

  return (
    <TabsContent
      userProfile={userProfile}
      onboardingConfiguration={onboardingConfiguration}
      account={account}
      accountId={accountId}
      rootPath={rootPath}
      renderData={renderData}
      onConfirm={getDetails}
    />
  );
};

export const AccountDetails = observer(TradingAccountDetails);
